import { Alert } from 'localmed-core';
import React from 'react';
import { ChevronRight } from 'react-feather';
import { Link, useLocation } from 'react-router-dom';
import { LeafletMarker } from '../../../shared/components/LeafletMap';
import isIOS from '../../../shared/utils/isIOS';
import { buildOfficeSchedulingUrl } from '../../../shared/utils/urls';
import activeNames from '../../utils/activeNames';
import { Office } from '../OfficeListMapV2/types';
import style from './OfficeListMapV1.scss';

interface OfficeListProps {
  activeOffice: Office | null;
  onOfficeHover: (office: Office | null) => void;
  offices: Office[];
  hasSelectedLocation?: boolean;
}

export default function OfficeList({
  offices,
  activeOffice,
  onOfficeHover,
  hasSelectedLocation,
}: OfficeListProps) {
  const location = useLocation();
  if (!offices || offices.length === 0) {
    return (
      <div className="padding-md">
        <Alert tier={hasSelectedLocation ? 'warning' : 'error'} subtle>
          {hasSelectedLocation ? (
            <>Sorry, we could not find any offices in&nbsp;your&nbsp;area.</>
          ) : (
            <>Sorry, we could not find any offices.</>
          )}
        </Alert>
      </div>
    );
  }

  return (
    <ul className={style['practice-list']}>
      {offices.map((office, index) => {
        const isActive = activeOffice != null && activeOffice.id === office.id;
        const mouseEventProps = !isIOS()
          ? {
              onMouseEnter: () => onOfficeHover(office),
              onMouseLeave: () => onOfficeHover(null),
            }
          : {};
        return (
          <li
            key={office.id}
            className={activeNames(isActive, 'practice-list__item', style)}
            data-qa="practice-widget__office"
          >
            <Link
              className={style['practice-list__item__anchor']}
              to={buildOfficeSchedulingUrl(office.id, { location, params: { exp_widget_v2: '' } })}
              {...mouseEventProps}
            >
              <div style={{ position: 'relative' }}>
                <LeafletMarker
                  isActive={isActive}
                  number={index}
                  marker={{
                    id: office.id,
                    latitude: office.latitude,
                    longitude: office.longitude,
                  }}
                />
              </div>

              <div className={style['practice-list__item__body']}>
                <h2 className={activeNames(isActive, 'practice-list__item__title', style)}>
                  {office.display}
                </h2>
                <p>{office.address1}</p>
                <p>{office.address2}</p>
              </div>
              <div className={activeNames(isActive, 'practice-list__item__icon', style)}>
                <ChevronRight size="100%" />
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
